import type { ContributorRole, RoleDto, RoleSlotDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { getObjectId, resolveRefId } from '@/helpers/object.id.helper';

const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'id' in value) {
    const id = (value as { id?: string }).id;
    return id ?? undefined;
  }
  return undefined;
};

export function resolveFlowIdForContributorRoleSlot(
  role: RoleDto<ContributorRole>,
  slot: RoleSlotDto,
  eventFlows?: FlowDto[],
): string | undefined {
  if (role.entityType === 'Flow') {
    const entity = role.entity;
    if (typeof entity === 'string') return entity;
    if (entity && typeof entity === 'object') {
      return entity.origin ? getObjectId(entity.origin) : entity.id;
    }
  }

  if (!eventFlows?.length) return undefined;

  if (slot.entityType === 'FlowSlot') {
    const flowSlotId = typeof slot.entity === 'string' ? slot.entity : slot.entity?.id;
    const flow = eventFlows.find((entry) => entry.slots?.some((flowSlot) => flowSlot.id === flowSlotId));
    if (flow) {
      return flow.origin ? getObjectId(flow.origin) : flow.id;
    }
  }

  if (role.entityType === 'Session') {
    const sessionId = typeof role.entity === 'string' ? role.entity : role.entity?.id;
    const sessionOrigin =
      typeof role.entity === 'object' && role.entity && 'origin' in role.entity
        ? resolveRefId((role.entity as { origin?: unknown }).origin as Parameters<typeof resolveRefId>[0])
        : undefined;
    const flow = eventFlows.find((entry) =>
      entry.slots?.some((flowSlot) => {
        if (flowSlot.contentType !== 'Session') return false;
        const contentId = typeof flowSlot.content === 'string' ? flowSlot.content : flowSlot.content?.id;
        const contentOrigin =
          typeof flowSlot.content === 'object' && flowSlot.content && 'origin' in flowSlot.content
            ? resolveRefId((flowSlot.content as { origin?: unknown }).origin as Parameters<typeof resolveRefId>[0])
            : undefined;
        return (
          contentId === sessionId ||
          contentId === sessionOrigin ||
          contentOrigin === sessionId ||
          contentOrigin === sessionOrigin
        );
      }),
    );
    if (flow) {
      return flow.origin ? getObjectId(flow.origin) : flow.id;
    }
  }

  if (slot.eventSlotId) {
    const eventSlotId = normalizeId(slot.eventSlotId);
    const flow = eventFlows.find((entry) =>
      entry.slots?.some((flowSlot) => {
        // Organize/plan flow slots may carry eventSlotId even when absent from FlowSlotDto.
        const linkedEventSlotId = normalizeId((flowSlot as { eventSlotId?: string }).eventSlotId);
        return linkedEventSlotId === eventSlotId;
      }),
    );
    if (flow) {
      return flow.origin ? getObjectId(flow.origin) : flow.id;
    }
  }

  return undefined;
}
